import {
    isPanelCell,
    locateViewport,
    planeOfCell,
    rowCanRotate,
} from './layout';
import { rotateRowPlanes } from './mutations';
import { Layout, Plane, ViewportId } from './types';

/** The message id of the refusal when no row is the target of the plane key. */
export const NO_PLANE_CYCLE_TARGET_MESSAGE_ID = 'cyclePlaneNoTarget';

/** The message id of the refusal while a panel is maximized over the rows. */
export const MAXIMIZED_PANEL_PLANE_CYCLE_MESSAGE_ID = 'cyclePlaneRestoreGrid';

/** The message id of the reason a row of one plane has no plane to cycle to. */
export const ONE_PLANE_ROW_MESSAGE_ID = 'cyclePlaneOnePlane';

/** A plane cycle that did not happen, with the message id of the reason. */
export type PlaneCycleRefusal = {
    readonly kind: 'refused';
    readonly messageId: string;
};

/**
 * What one plane cycle did: the planes moved one step, the cycle was left
 * alone because a tool action is in progress, or it was refused with the
 * message id of the reason.
 */
export type PlaneCycleOutcome =
    | { readonly kind: 'cycled' }
    | { readonly kind: 'ignored' }
    | PlaneCycleRefusal;

/**
 * The plane control a tile offers for its row: the plane the tile shows
 * after one cycle, `undefined` when the row shows one plane and has none to
 * cycle to, and the cycle itself.
 */
export interface PlaneCycleControl {
    readonly nextPlane: Plane | undefined;
    readonly onCycle: () => void;
}

/** The refusal of a plane cycle for the reason `messageId` names. */
export function refusedWith(messageId: string): PlaneCycleRefusal {
    return { kind: 'refused', messageId };
}

/**
 * What the plane key acts on: the index of one row, or no row with the
 * message id of the reason.
 */
export type PlaneCycleTarget =
    | { readonly kind: 'row'; readonly rowIndex: number }
    | PlaneCycleRefusal;

function targetRow(rowIndex: number): PlaneCycleTarget {
    return { kind: 'row', rowIndex };
}

/**
 * The plane a pane shows after its row's planes cycle one step, or
 * `undefined` when its row shows one plane (a side-by-side pair drawing
 * one plane twice included) or the pane is no plane pane of the layout.
 */
export function nextPlaneOfPane(
    layout: Layout,
    id: ViewportId
): Plane | undefined {
    const located = locateViewport(layout, id);
    if (!located || !rowCanRotate(located.row)) {
        return undefined;
    }
    const cycled = locateViewport(
        rotateRowPlanes(layout, located.rowIndex),
        id
    );
    return cycled ? planeOfCell(cycled.cell) : undefined;
}

/**
 * The index of the bound row whose pane `id` names, or `undefined` when the
 * id names no pane of a bound row.
 */
function boundRowOf(
    layout: Layout,
    id: ViewportId | undefined
): number | undefined {
    const located = id ? locateViewport(layout, id) : undefined;
    return located?.row.dataset ? located.rowIndex : undefined;
}

/**
 * The row the plane key cycles: the row of the maximized image tile; with
 * no tile maximized, the row of the active tile; with no active tile, the
 * one bound row when exactly one row is bound. Refused when none of these
 * names a row. A maximized panel is refused with its own reason: it hides
 * every image row, so a cycle there would change planes the user cannot
 * see.
 */
export function planeCycleTarget(
    layout: Layout,
    maximizedId: ViewportId | undefined,
    activeId: ViewportId | undefined
): PlaneCycleTarget {
    if (maximizedId) {
        const located = locateViewport(layout, maximizedId);
        if (located && isPanelCell(located.cell)) {
            return refusedWith(MAXIMIZED_PANEL_PLANE_CYCLE_MESSAGE_ID);
        }
        const maximizedRow = boundRowOf(layout, maximizedId);
        return maximizedRow === undefined
            ? refusedWith(NO_PLANE_CYCLE_TARGET_MESSAGE_ID)
            : targetRow(maximizedRow);
    }
    const activeRow = boundRowOf(layout, activeId);
    if (activeRow !== undefined) {
        return targetRow(activeRow);
    }
    const boundRows = layout.rows.flatMap((row, index) =>
        row.dataset ? [index] : []
    );
    return boundRows.length === 1
        ? targetRow(boundRows[0])
        : refusedWith(NO_PLANE_CYCLE_TARGET_MESSAGE_ID);
}
